import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { initDevToolsProtection } from '../utils/devToolsProtection';
import './LoginPage.css';

function LoginPage({ onRequestAccess }) {
  const [screen, setScreen] = useState('login'); // 'login' | 'reset'

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');

  const [resetEmail, setResetEmail]     = useState('');
  const [resetSent, setResetSent]       = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  // ── Activate DevTools protection the moment login page mounts ─────────────
  useEffect(() => {
    initDevToolsProtection({ role: 'technician' });
  }, []);

  // ── Sign in ─────────────────────────────────────────────────────────────
  // On success, App.js's auth-state listener takes over automatically —
  // it checks approval status and routes to the dashboard, the "awaiting
  // approval" screen, or the "denied" screen as appropriate.
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
        setError('Incorrect email or password.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a moment and try again.');
      } else {
        setError('Connection error. Please check your internet and try again.');
      }
      setLoading(false);
    }
  };

  // ── Forgot password ─────────────────────────────────────────────────────
  // Firebase sends the reset email itself — no server code needed on our side.
  const handleResetSubmit = async (e) => {
    e.preventDefault();

    if (!resetEmail.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendPasswordResetEmail(auth, resetEmail.trim());
      setResetSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      // Intentionally vague on "user not found" so this can't be used to
      // check which emails have accounts. Firebase itself already treats
      // this case as a success from the caller's point of view in most
      // configurations, but we handle it defensively either way.
      if (err.code === 'auth/invalid-email') {
        setError('That email address doesn\u2019t look valid.');
      } else {
        setResetSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const goToReset = () => {
    setResetEmail(email);
    setResetSent(false);
    setError('');
    setScreen('reset');
  };

  const backToLogin = () => {
    setError('');
    setScreen('login');
  };

  return (
    <div className="login-page">

      {/* Full-bleed photographic background (swap the image at /public/login-bg.jpg).
          NOTE: this is set via inline style + PUBLIC_URL, not CSS url(), because
          Create React App's webpack config can't resolve public/ assets from
          inside a bundled .css file — only from JS. */}
      <div
        className="login-bg-photo"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/login-bg.jpg)` }}
      ></div>
      <div className="login-bg-scrim"></div>

      {/* Subtle grain texture only — the animated diagonal accent lines and
          floating silhouettes were removed since they crossed visibly over
          the real photo (they were designed for the flat gradient backdrop). */}
      <div className="login-background">
        <div className="grain-overlay"></div>
      </div>

      <div className="login-container">

        {/* Logo */}
        <div className="login-logo">
          <div className="logo-container">
            <h1 className="dilex-wordmark">dilex</h1>
            <div className="grain-accent-line"></div>
          </div>
          <p className="login-subtitle">Operations Management System</p>
        </div>

        {screen === 'login' && (
          <form onSubmit={handleSubmit} className="login-form">
            <h2>Secure Access</h2>

            <div className="input-container">
              <span className="input-icon">&#9993;&#65039;</span>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                className="text-input"
                autoFocus
                disabled={loading}
              />
            </div>

            <div className="input-container">
              <span className="input-icon">&#128272;</span>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                className="text-input"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="error-message">
                <span>&#9888;&#65039;</span> {error}
              </div>
            )}

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="button-spinner"></span>
                  Signing in...
                </>
              ) : (
                'ACCESS SYSTEM'
              )}
            </button>

            <div className="login-links" style={{ textAlign: 'center' }}>
              <a href="#" className="forgot-link" onClick={(e) => { e.preventDefault(); goToReset(); }}>
                Forgot password?
              </a>
            </div>
          </form>
        )}

        {screen === 'reset' && (
          resetSent ? (
            <div className="status-card">
              <div className="status-icon">&#128231;</div>
              <h2 className="status-title">Check Your Email</h2>
              <p className="status-message">
                If an account exists for <strong>{resetEmail}</strong>, a password
                reset link has been sent. Follow the link in that email to choose
                a new password, then come back here to sign in.
              </p>
              <button type="button" className="login-button" onClick={backToLogin}>
                Back to Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleResetSubmit} className="login-form">
              <h2>Reset Password</h2>

              <div className="input-container">
                <span className="input-icon">&#9993;&#65039;</span>
                <input
                  type="email"
                  placeholder="Your email address"
                  value={resetEmail}
                  onChange={(e) => { setResetEmail(e.target.value); setError(''); }}
                  className="text-input"
                  autoFocus
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="error-message">
                  <span>&#9888;&#65039;</span> {error}
                </div>
              )}

              <button type="submit" className="login-button" disabled={loading}>
                {loading ? (
                  <>
                    <span className="button-spinner"></span>
                    Sending...
                  </>
                ) : (
                  'SEND RESET LINK'
                )}
              </button>

              <div className="login-links" style={{ textAlign: 'center' }}>
                <a href="#" className="forgot-link" onClick={(e) => { e.preventDefault(); backToLogin(); }}>
                  Back to Login
                </a>
              </div>
            </form>
          )
        )}

        {/* Footer */}
        <div className="login-footer">
          <div className="footer-divider"></div>
          <button type="button" className="access-notice access-notice-link" onClick={onRequestAccess}>
            New here? Request Access
          </button>
          <div className="footer-micro">
            <span className="version-text">Powered by DILEX Holdings</span>
          </div>
        </div>

      </div>
    </div>
  );
}

export default LoginPage;
